<script setup lang="ts">
import { computed } from 'vue';
import AdminAppLinkColumn from '@/components/AdminAppLinkColumn.vue';
import DateColumn from '@/components/DateColumn.vue';
import ForeignModelColumn from '@/components/ForeignModelColumn.vue';
import ChildrenArrayColumn from '@/components/ChildrenArrayColumn.vue';
import DefaultColumn from '@/components/DefaultColumn.vue';
import CoverArtColumn from '@/components/CoverArtColumn.vue';
import { getLiveTrackCellColumn } from '@/components/cellColumns';
import { isModelArrayDataType } from '@/types/columnTypes';
import JsonModels from '@/ts/models.json';
import { useEntityStore } from '@/stores/entityStore';
import type { JsonModel } from '@/types/columnTypes';
import OverflowTooltip from '@/components/OverflowTooltip.vue';
import { filterControlMappings } from '@/components/FilterBuilder/filterControlRegistry';
import { ID_LOOKUP_OPTIONS, idLookupDisplayString } from '@/constants/lookupFilterOptions';
import { polymorphicModelTypes, resolvePolymorphicModelType } from '@/utils/polymorphicModelType';
import StatusChip from '@/components/DataTable/StatusChip.vue';
import PasswordValue from '@/components/record/fields/PasswordValue.vue';
import { isPasswordColumn } from '@/components/record/fields/fieldKind';
import { isStatusColumn, statusWord } from '@/components/DataTable/statusChips';
import ScheduleFieldCell from '@/components/schedules/ScheduleFieldCell.vue';
import { customListScheduleFieldOf } from '@/components/FilterBuilder/filterControlRegistry';

/**
 * Optional overrides for foreign key columns when the displayed entity shape
 * doesn't match models.json (e.g. join/summary views). Keyed by entity name,
 * then by field name, value is the foreign model type.
 * Example: { Artist: { album_id: 'Album' } }
 *
 * effectiveEntityId: When provided (e.g. by TrackMatchingColumnMapping), use this
 * instead of entityStore.selectedEntityId for model lookup. Avoids mutating global
 * entity state when a view uses a different schema (e.g. track-matching always uses LiveTrack).
 */
const props = withDefaults(
  defineProps<{
    field: string;
    dataType: string;
    data: Record<string, unknown>;
    foreignKeyOverrides?: Record<string, Record<string, string>>;
    effectiveEntityId?: string;
  }>(),
  { foreignKeyOverrides: () => ({}) }
);

const entityStore = useEntityStore();

/** A login password draws masked, with a button that shows it. See PasswordValue. */
const isPassword = computed(() => isPasswordColumn({ dataType: props.dataType }));
/** The row's id. Showing the password in one row's cell leaves every other row masked. */
const recordKey = computed(() => (props.data as Record<string, unknown> | null)?.id as string | number | undefined);

/**
 * A custom list's schedule column, such as `location_zone_custom_playlist_schedules.date`,
 * draws one value per schedule. It is drawn before any other branch reads the field, because
 * `location_zone_id` would read as a foreign key and `date` as a single date.
 */
const scheduleField = computed(() => customListScheduleFieldOf(props.field));

const isLiveTrackTableContext = computed(() => {
  const entityKey = (props.effectiveEntityId ?? entityStore.selectedEntityId).replace(/\s+/g, '');
  return entityKey === 'LiveTrack';
});

/**
 * Integer FK columns backed by a bundled id lookup (brand_id, live_track_action_status_id,
 * raw_ddex_track_takedown_status_id) have no relation column in models.json to link
 * through — render the option's display string ("TPG", not 2). Gated on the column
 * config, not the value, so these fields never fall into the foreignModelType path
 * (which would warn about the missing relation).
 */
const idLookupOptionsSource = computed(() => {
  const currentModelName = (props.effectiveEntityId ?? entityStore.selectedEntityId).replace(/\s+/g, '');
  const optionsSource = filterControlMappings[currentModelName]?.[props.field]?.optionsSource;
  return optionsSource && optionsSource in ID_LOOKUP_OPTIONS ? optionsSource : null;
});

const idLookupDisplayValue = computed(() => {
  if (!idLookupOptionsSource.value) {
    return null;
  }
  const rawValue = (props.data as Record<string, unknown> | null)?.[props.field];
  // Unknown id (options list out of date): degrade to the raw value, never blank.
  return idLookupDisplayString(idLookupOptionsSource.value, rawValue) ?? rawValue ?? null;
});

/**
 * Status columns draw as a chip: a coloured dot and the word, such as
 * "● Offline" (SAU-437). A plain `*_id` column keeps its link, unless it is a
 * bundled lookup, whose name is the status word. An empty value falls through
 * to the normal "—".
 */
const statusChipWord = computed(() => {
  if (!isStatusColumn(props.field)) {
    return null;
  }
  if (idLookupOptionsSource.value) {
    const word = idLookupDisplayValue.value;
    return word === null || word === undefined ? null : String(word);
  }
  if (props.field.endsWith('_id')) {
    return null;
  }
  return statusWord(props.field, (props.data as Record<string, unknown> | null)?.[props.field.split('.')[0]]);
});

// Detect foreign key ID columns and get the foreign model type
const foreignModelType = computed(() => {
  // Check if this is a foreign key ID column (ends with _id and is number type)
  if (props.field.endsWith('_id') && props.dataType === 'number') {
    const currentModelName = (props.effectiveEntityId ?? entityStore.selectedEntityId).replace(/\s+/g, '');
    const currentModel = JsonModels[currentModelName as keyof typeof JsonModels] as JsonModel | undefined;
    
    if (!currentModel) {
      return null;
    }
    
    // Check overrides first (e.g. track matching / join views where entity shape differs from models.json)
    const overrideType = props.foreignKeyOverrides?.[currentModelName]?.[props.field];
    if (overrideType) {
      return overrideType;
    }

    // Extract the foreign model column name (e.g., 'location_zone' from 'location_zone_id')
    const foreignModelColumnName = props.field.slice(0, -3); // Remove '_id' suffix
    
    // Look up the foreign model column in the current model
    const foreignModelColumnType = currentModel[foreignModelColumnName];
    
    if (!foreignModelColumnType) {
      // A *_id field without a matching relation column in models.json (e.g.
      // LocationZonePlayerParsedPlaylistTrack.track_id). Degrade to a plain value
      // instead of throwing: the inspector renders every column regardless of
      // Column Settings, so a render-time throw would crash the whole detail pane
      // with no UI workaround.
      console.warn(
        `Foreign key ID column '${props.field}' in model '${currentModelName}' has no ` +
        `matching '${foreignModelColumnName}' relation column in models.json; rendering as a plain value.`
      );
      return null;
    }
    
    // A relation that can be one of several models, such as a station's source. The id alone
    // does not say which table it points into, so link by the reference's own type. With no
    // type to go on, show the plain id: a guess opens an unrelated record with the same number.
    if (polymorphicModelTypes(foreignModelColumnType)) {
      return resolvePolymorphicModelType(foreignModelColumnType, props.data?.[foreignModelColumnName]);
    }

    // Extract model name from the foreign model column type (e.g., "LocationZone" from "LocationZone")
    // Handle both single model types and array types
    const modelType = typeof foreignModelColumnType === 'string' 
      ? foreignModelColumnType.replace('[]', '') 
      : null;
    
    if (modelType && modelType[0] === modelType[0].toUpperCase()) {
      return modelType;
    }
    
    return null;
  }
  
  return null;
});

const columnComponent = computed(() => {
  if (props.field === 'id') {
    return AdminAppLinkColumn;
  } else if (foreignModelType.value) {
    // Foreign key ID column - use AdminAppLinkColumn with foreignModelType
    return AdminAppLinkColumn;
  } else if (props.dataType === 'Date') {
    return DateColumn;
  } else if (isModelArrayDataType(props.dataType) && props.field.endsWith('.display_string')) {
    // Children array fields (e.g., 'albums.display_string' where dataType is 'Album[]')
    return ChildrenArrayColumn;
  } else if (isLiveTrackTableContext.value && getLiveTrackCellColumn(props.field)) {
    // Custom LiveTrack cell renderers (lyrics status, playlist count, rating, ...)
    // live in the cellColumns registry — same pattern as arrayColumnRegistry.
    return getLiveTrackCellColumn(props.field)!;
  } else if (props.dataType[0] === props.dataType[0].toUpperCase() || props.field.includes('.')) { // artists.name or user.display_string
    return ForeignModelColumn;
  } else if (props.field === 'cover_art_signed_url') {
    return CoverArtColumn;
  } else {
    return DefaultColumn;
  }
});

/**
 * The data type the cell renders with. For a column that can hold one of several models,
 * such as `contact_login_station_source.display_string`, it is the model the row's own
 * reference names, so the value opens the right kind of record. Otherwise it is the column's.
 */
const renderedDataType = computed(() => {
  if (!polymorphicModelTypes(props.dataType)) {
    return props.dataType;
  }
  const relationName = props.field.split('.')[0];
  return resolvePolymorphicModelType(props.dataType, props.data?.[relationName]) ?? props.dataType;
});

// Only AdminAppLinkColumn accepts foreignModelType; passing it to others causes Vue "extraneous non-props" warnings
const componentProps = computed(() => {
  const base = { data: props.data, field: props.field, dataType: renderedDataType.value };
  if (columnComponent.value === AdminAppLinkColumn) {
    return { ...base, foreignModelType: foreignModelType.value || undefined };
  }
  return base;
});
</script>

<template>
  <PasswordValue v-if="isPassword" :value="data[field]" label="Password" :record-key="recordKey" />
  <ScheduleFieldCell v-else-if="scheduleField" :row="data" :field="scheduleField" />
  <StatusChip v-else-if="statusChipWord" :word="statusChipWord" />
  <OverflowTooltip v-else-if="idLookupOptionsSource" :deps="idLookupDisplayValue">{{
    idLookupDisplayValue
  }}</OverflowTooltip>
  <component :is="columnComponent" v-bind="componentProps" v-else />
</template>
